import {MantineColor, MantineTheme} from '@mantine/core';

import {CostBreakdownKey} from 'notebook/tables/excess/CostAnalyzer';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {FormulaCostTier, TableLimitUtils, TablePerformanceSummary} from './tableLimitUtils';

type BadgeText = 'Good' | 'Nearing Limit' | 'Over Limit';
type BadgeColor = Extract<MantineColor, 'red' | 'orange' | 'green'>;
type RingIcon = Extract<BenchlingIconName, 'check' | 'exclamation-triangle' | 'exclamation-circle'>;

function pluralizeNoun(noun: string): string {
  return noun === 'entry' ? 'entries' : `${noun}s`;
}

function overallExceededLimitMessage(noun: string): string {
  return `This ${noun} has exceeded its performance limit, based on table cell count and formula complexity. You may already be experiencing slowness — reduce table size or formula use to improve performance. Consider splitting this ${noun} into multiple separate ${pluralizeNoun(noun)} or removing unnecessary tables.`;
}
function overallNearingLimitMessage(noun: string): string {
  return `This ${noun} is approaching its performance limit, based on table cell count and formula complexity. Avoid adding more rows or complex formulas to prevent slowness. Consider splitting this ${noun} into multiple separate ${pluralizeNoun(noun)} or removing unnecessary tables.`;
}
function overallGoodMessage(noun: string): string {
  return `This ${noun}'s size and complexity are within the recommended limits.`;
}

function tableHighestImpactCellsMessage(noun: string): string {
  return `This table is the biggest contributor to your ${noun}'s performance score, driven mainly by its table cell count. Consider removing rows or columns that are not needed.`;
}
function tableHighestImpactMixedMessage(noun: string): string {
  return `This table is the biggest contributor to your ${noun}'s performance score — both its table cell count and formula complexity are high. Consider removing rows or columns that are not needed, simplifying formulas, or moving redundant computations to a separate table.`;
}
function tableHighestImpactFormulasMessage(noun: string): string {
  return `This table is the biggest contributor to your ${noun}'s performance score, driven mainly by its formula complexity. Consider simplifying formulas or moving redundant computations to a separate table.`;
}
const TABLE_CELLS_AND_FORMULAS_MESSAGE =
  'This table has a high performance score driven by both its size and complex formulas. Consider removing rows or columns that are not needed, simplifying formulas, or moving redundant computations to a separate table.';
const TABLE_CELLS_ONLY_MESSAGE =
  "This table's score is driven mainly by its table cell count. Consider removing rows or columns that are not needed.";
const TABLE_FORMULAS_ONLY_MESSAGE =
  "This table's score is driven mainly by its formula complexity. Consider simplifying formulas or moving redundant computations to a separate table.";
function tableLowImpactMessage(noun: string): string {
  return `This table has a low performance score — its table cell count and formula complexity are not significantly impacting your ${noun}.`;
}

function additionalManyTablesMessage(noun: string): string {
  return `This ${noun} has many tables. Try splitting it into multiple ${pluralizeNoun(noun)} or removing unnecessary tables.`;
}

export interface ComputedModalRowInfo {
  pct: number;
  alertTitle: string;
  styleMessageAsAlert: boolean;
  message: string;
  markerColor: string;
  costTier: FormulaCostTier;
}

export interface ComputedModalInfo {
  ringColor: string;
  ringIcon: RingIcon;
  badgeColor: BadgeColor;
  badgeText: BadgeText;
  message: string;
  additionalMessage: string;

  tables: Record<string, ComputedModalRowInfo>;
}

function getGreen(theme: MantineTheme): string {
  return theme.colors.green[6];
}

function getOrange(theme: MantineTheme): string {
  return theme.colors.orange[6];
}

function getRed(theme: MantineTheme): string {
  return theme.colors.red[8];
}

function getOverallSeverityMap(
  theme: MantineTheme,
  noun: string
): Record<
  FormulaCostTier,
  {
    ringColor: string;
    ringIcon: RingIcon;
    badgeColor: BadgeColor;
    badgeText: BadgeText;
    message: string;
  }
> {
  return {
    good: {
      ringColor: getGreen(theme),
      ringIcon: 'check',
      badgeColor: 'green',
      badgeText: 'Good',
      message: overallGoodMessage(noun),
    },
    nearing_limit: {
      ringColor: getOrange(theme),
      ringIcon: 'exclamation-triangle',
      badgeColor: 'orange',
      badgeText: 'Nearing Limit',
      message: overallNearingLimitMessage(noun),
    },
    over_limit: {
      ringColor: getRed(theme),
      ringIcon: 'exclamation-circle',
      badgeColor: 'red',
      badgeText: 'Over Limit',
      message: overallExceededLimitMessage(noun),
    },
  };
}

function getTableSeverityMap(theme: MantineTheme): Record<
  FormulaCostTier,
  {
    markerColor: string;
    styleMessageAsAlert: boolean;
    alertTitle: string;
  }
> {
  return {
    good: {
      markerColor: getGreen(theme),
      styleMessageAsAlert: false,
      alertTitle: '',
    },
    nearing_limit: {
      markerColor: getOrange(theme),
      styleMessageAsAlert: true,
      alertTitle: "How to reduce this table's impact",
    },
    over_limit: {
      markerColor: getRed(theme),
      styleMessageAsAlert: true,
      alertTitle: "How to reduce this table's impact",
    },
  };
}

function getTablePerformanceMessage(
  costBreakdown: {[key: string]: number},
  isMostExpensiveTable: boolean,
  tableFormulaCostTier: FormulaCostTier,
  noun: string
): string {
  if (tableFormulaCostTier === 'good') {
    return tableLowImpactMessage(noun);
  }

  const sortedCostBreakdown = Object.entries(costBreakdown).sort((a, b) => b[1] - a[1]);
  const topThree: {[key: string]: number} = {};
  for (const [key, value] of sortedCostBreakdown.slice(0, 3)) {
    topThree[key] = value;
  }

  const topThreeTotalCount = Object.values(topThree).reduce((acc, value) => acc + value, 0);

  // if cell count is not in the top three, set it to 0
  const cellCountPct = (topThree[CostBreakdownKey.CELL_COUNT] ?? 0) / (topThreeTotalCount || 1);
  if (isMostExpensiveTable) {
    if (cellCountPct > 0.8) {
      return tableHighestImpactCellsMessage(noun);
    } else if (cellCountPct > 0.5) {
      return tableHighestImpactMixedMessage(noun);
    } else {
      return tableHighestImpactFormulasMessage(noun);
    }
  } else if (cellCountPct > 0.8) {
    return TABLE_CELLS_ONLY_MESSAGE;
  } else if (cellCountPct > 0.5) {
    return TABLE_CELLS_AND_FORMULAS_MESSAGE;
  } else {
    return TABLE_FORMULAS_ONLY_MESSAGE;
  }
}

function computeTableRowInfo(
  row: TablePerformanceSummary,
  overallFormulaCost: number,
  isMostExpensiveTable: boolean,
  overallFormulaCostTier: FormulaCostTier,
  theme: MantineTheme,
  noun: string
): ComputedModalRowInfo {
  const {formulaCost, costBreakdown} = row;
  const tableFormulaCostTier = constrainTableFormulaCostTier(
    TableLimitUtils.getFormulaCostTierForTable(formulaCost),
    overallFormulaCostTier
  );
  const {alertTitle, styleMessageAsAlert, markerColor} = getTableSeverityMap(theme)[tableFormulaCostTier];
  const message = getTablePerformanceMessage(costBreakdown, isMostExpensiveTable, tableFormulaCostTier, noun);
  const pct = overallFormulaCost > 0 ? formulaCost / overallFormulaCost : 0;
  return {
    alertTitle,
    message,
    styleMessageAsAlert,
    markerColor,
    costTier: tableFormulaCostTier,
    pct,
  };
}

/** We don't want to show yellow/red tables if the overall status is good/green. */
function constrainTableFormulaCostTier(
  tableFormulaCostTier: FormulaCostTier,
  overallFormulaCostTier: FormulaCostTier
): FormulaCostTier {
  if (overallFormulaCostTier === 'good') {
    return 'good';
  } else {
    return tableFormulaCostTier;
  }
}

function getAdditionalOverallPerformanceMessage(
  overallFormulaCostTier: FormulaCostTier,
  tables: Record<string, ComputedModalRowInfo>,
  noun: string
): string {
  if (overallFormulaCostTier !== 'good') {
    const areAllTablesGood = Object.values(tables).every((table) => table.costTier === 'good');
    if (areAllTablesGood) {
      return additionalManyTablesMessage(noun);
    }
  }
  return '';
}

export function computeModalInfo(
  overallFormulaCost: number,
  rows: Array<TablePerformanceSummary>,
  theme: MantineTheme,
  noun: string
): ComputedModalInfo {
  const overallFormulaCostTier = TableLimitUtils.getFormulaCostTier(overallFormulaCost);
  const overallSeverityMap = getOverallSeverityMap(theme, noun);
  const overallSeverity = overallSeverityMap[overallFormulaCostTier];
  const costOfMostExpensiveTable = rows.reduce((max, row) => Math.max(max, row.formulaCost), 0);

  const tables: Record<string, ComputedModalRowInfo> = {};
  let foundMostExpensive = false;
  for (const row of rows) {
    const isMostExpensiveTable: boolean = row.formulaCost === costOfMostExpensiveTable && !foundMostExpensive;
    foundMostExpensive = foundMostExpensive || isMostExpensiveTable;
    const tablePerformanceMessage = computeTableRowInfo(
      row,
      overallFormulaCost,
      isMostExpensiveTable,
      overallFormulaCostTier,
      theme,
      noun
    );
    tables[row.tableId] = tablePerformanceMessage;
  }

  const additionalMessage = getAdditionalOverallPerformanceMessage(overallFormulaCostTier, tables, noun);

  return {
    ...overallSeverity,
    additionalMessage,
    tables,
  };
}

export const _forTesting = {
  getTablePerformanceMessage,
  constrainTableFormulaCostTier,
  getAdditionalOverallPerformanceMessage,
};
